import _ from "lodash";
import ReactDOMServer from "react-dom/server";
import { Base64 } from "js-base64";
import watermark from "watermarkjs";
import { fetch } from "lib/fetch";
import { format } from "lib/globalize-util";
import { SchemaRecord } from "records/schema/schema";
import { IDataAttribute } from "reducers/watermarking";
import { createMarkup } from "lib/helpers";

export const WATERMARK_STRIP_WIDTH = 1200;

export const EMPTY_OPTION_KEY = "_none";
export const getEmptyOption: () => IDataAttribute & { name: string } = () => ({
    key: EMPTY_OPTION_KEY,
    name: format("None"),
});

/**
 * Combines two images, applies overlay image with 45 degrees rotation. Works in scope of watermarkjs package.
 * @param target - Canvas element containing source image.
 * @param overlay - Canvas element containing image to be applied on the target.
 * @returns Target canvas with image modified.
 */
export const applyWatermarkToImage = (target: HTMLCanvasElement, overlay: HTMLCanvasElement) => {
    const context = target.getContext("2d");
    context.save();

    // calculate diagonal of the image and rotation angle of the diagonal
    const diagonal = Math.sqrt(target.width ** 2 + target.height ** 2);
    const rotationAngle = -Math.atan(target.height / target.width);

    // scale watermark to fit the image
    const overlayAspectRatio = overlay.height / overlay.width;
    const [overlayWidth, overlayHeight] = [diagonal, diagonal * overlayAspectRatio];

    // move (0,0) point of canvas to bottom left of the image and rotate it depending on image dimensions
    const translateX = -Math.abs((overlayHeight / 2) * Math.sin(rotationAngle));
    const translateY = target.height - Math.abs((overlayHeight / 2) * Math.cos(rotationAngle));
    context.translate(translateX, translateY);
    context.rotate(rotationAngle);

    // draw the image in the new starting point of rotated canvas
    context.drawImage(overlay, 0, 0, overlayWidth, overlayHeight);

    // reset canvas starting point back to default in case we need to draw something else
    context.translate(-translateX, -translateY);
    context.restore();

    return target;
};

/**
 * Encodes SVG into Base64 string
 * @param reactElement - Element to be processed.
 * @returns Base64 url.
 */
export const encodeSvg = (reactElement) => {
    return "data:image/svg+xml;base64," + Base64.encode(ReactDOMServer.renderToStaticMarkup(reactElement));
};

/** Appends element to the page and returns it's dimensions */
export const getElementDimensions = (element): [number, number] => {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.top = "-1000";
    div.style.left = "-1000";
    div.style.display = "flex";
    div.style.flexDirection = "column";

    div.innerHTML = createMarkup(ReactDOMServer.renderToString(element));
    document.body.append(div);

    const dimensions = [div.clientWidth, div.clientHeight] as [number, number];
    div.remove();

    return dimensions;
};

/** Calculates offsets depending on watermark width. Spreads them evenly. */
export const getOffsetsX = (watermarkWidth: number, minimalGap: number) => {
    const totalWatermarkWidth = watermarkWidth + Math.max(watermarkWidth / 4, minimalGap);

    const rawNumberOfCounters = WATERMARK_STRIP_WIDTH / totalWatermarkWidth;
    const [numberOfCounters, remainingSpace] = [
        Math.floor(rawNumberOfCounters),
        (rawNumberOfCounters % 1) * totalWatermarkWidth,
    ];

    const initialOffset = (remainingSpace + totalWatermarkWidth) / 2;
    return _.times(numberOfCounters, (index) => initialOffset + totalWatermarkWidth * index);
};

export const fetchBlob = (url: string) =>
    fetch(url, { noDomain: true }).then((data) =>
        Promise.all([data.blob(), data?.headers?.get("Content-Type")]).then(([blob, contentType]) => ({
            blob,
            contentType,
        }))
    );

export const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
};

type TProcessSingleImage = (src: string, svg: string, setError?: (message: string) => void) => Promise<string | null>;
export const processSingleImage: TProcessSingleImage = async (src, svg, setError) => {
    try {
        if (!src || src.startsWith("data:")) {
            return Promise.resolve(src);
        }
        const { blob: svgBlob } = await fetchBlob(svg);
        return new Promise((resolve) =>
            watermark([src, svgBlob], {
                init: (img: { crossOrigin: string; onerror: Function }) => {
                    img.crossOrigin = "anonymous"; // load cross domain images
                    img.onerror = () => {
                        setError?.(format("An error occurred while fetching preview image."));
                        return resolve(src); // fallback to image without watermark
                    };
                },
            })
                .dataUrl(applyWatermarkToImage)
                .then(resolve)
        );
    } catch (err) {
        setError?.(format("An error occurred while fetching preview image."));
        return src; // fallback to image without watermark
    }
};

export const getSchemaFields = (schemaEntities: { [key: string]: SchemaRecord }, schemaName: string) => {
    const defaultFields: (IDataAttribute & { name: string })[] = [
        getEmptyOption(),
        { key: "__name", name: format("Company Name"), schemaType: schemaName },
        { key: "__code", name: format("Company Code"), schemaType: schemaName },
    ];
    const schema = _.find(schemaEntities, (schema) => schema.get("type") === schemaName)?.toJS();

    // collect fields eligible for displaying on watermark
    return _.keyBy(
        defaultFields.concat(
            schema.fields
                .filter((field) => field.details.type === "single" && field.details.subtype === "string")
                .map(({ key, name }) => ({ key, name, schemaType: schemaName }))
        ),
        "key"
    );
};
